import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { DialogDismissGuardDirective } from '@shared/ui';

import type { MethodPipelineContext } from '../../../state/assigned-pipelines.state';
import { MethodTypeEditorOutcome, MethodTypeEditorState } from '../../../state/method-type-editor.state';
import {
  METHOD_PIPELINE_MANAGER_CONTEXT,
  MethodPipelineManagerFacade,
} from '../../pipelines/method-pipeline-manager/method-pipeline-manager.facade';
import {
  RESOURCE_MANAGER_CONTAINER_ID,
  ResourceManagerFacade,
} from '../../resources/resource-manager/resource-manager.facade';
import { EDITOR_COMMIT_MODE } from '../../editor-commit-mode.token';
import { MethodTypeEditorComponent } from './method-type-editor.component';

/**
 * Which method type this dialog instance edits — the id plus whether it is
 * already persisted. Passed as `MatDialog` data by the opener (the method-type
 * manager) so the dialog can scope its embedded managers to that method type
 * (see the providers below). It mirrors the editor's own session context, but
 * the dialog cannot read the editor context token (the manager owns the editor
 * scope and seeds it per open), so the opener restates it here. The opener
 * always knows the concrete id (a minted `tmp-` id for NEW, the row id for
 * edit), so it is required.
 */
export interface MethodTypeEditorDialogData {
  methodTypeId: string;
  persisted: boolean;
}

/**
 * Thin MatDialog host around the smart {@link MethodTypeEditorComponent}, so the
 * case-type method-type manager can open the editor as a dialog. Mirrors
 * `PipelineEditorDialogComponent` / `PipelineStepEditorDialogComponent`: it
 * renders only the editor and bridges the editor's one-shot outcome onto
 * `dialogRef.close(outcome)`.
 *
 * THE PER-OPEN CONTEXT BOUNDARY. The editor embeds a pipeline manager and a
 * resource manager, each of which reads its container token
 * (`METHOD_PIPELINE_MANAGER_CONTEXT` / `RESOURCE_MANAGER_CONTAINER_ID`) ONCE at
 * construction. A single manager session opens this dialog for many different
 * method types (NEW, then edit A, then edit B), so those tokens cannot be
 * statically wired by the manager host — they must follow whichever method type
 * THIS open targets. The dialog is recreated per open, so it is the natural seam:
 * it (re)provides those tokens and the two manager facades from the
 * `MAT_DIALOG_DATA` it was opened with. The facades inject their shared
 * State/Effects from the host scope up the injector chain; only the per-open
 * identity is fixed here.
 *
 * The editor session scope itself (`MethodTypeEditorState`, the editor trio, the
 * shared staging ledgers) stays with the host: the manager seeds the editor
 * BEFORE opening and reads the produced draft AFTER closing, so it must outlive
 * the dialog. This wrapper declares NO editor providers and does NOT seed — it
 * just bridges `state.outcome$` → `dialogRef.close(outcome)`.
 *
 * HOST CONTRACT: the host must provide the embedded managers' shared
 * State/Effects/ledgers but NOT their facades or context tokens — this dialog
 * OWNS the two facades + the two context tokens. (Provide the shared tier the
 * way the manager story does; do not spread `methodPipelineManagerProviders` /
 * `resourceManagerProviders` wholesale, which would double-provide the facades.)
 *
 * The editor's own discard-confirm (a nested `ConfirmationDialogComponent`)
 * already gates cancel; this wrapper just closes once the editor emits.
 * `DialogDismissGuardDirective` routes backdrop click / Escape through that
 * same `onCancel()` seam, so implicit dismissal gets the same discard warning
 * and the dialog always closes with an explicit outcome (never `undefined`).
 */
@Component({
  selector: 'ts-method-type-editor-dialog',
  template: `<ts-method-type-editor
    #editor
    tsDialogDismissGuard
    (dismissRequested)="editor.onCancel()"
  ></ts-method-type-editor>`,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatDialogModule, MethodTypeEditorComponent, DialogDismissGuardDirective],
  providers: [
    // Dialog host = staging surface: the editor's confirm button reads
    // "Continue" here, "Save" only where a page actually persists.
    { provide: EDITOR_COMMIT_MODE, useValue: 'stage' },
    MethodPipelineManagerFacade,
    ResourceManagerFacade,
    {
      provide: METHOD_PIPELINE_MANAGER_CONTEXT,
      useFactory: (data: MethodTypeEditorDialogData): MethodPipelineContext => ({
        methodTypeId: data.methodTypeId,
        persisted: data.persisted,
      }),
      deps: [MAT_DIALOG_DATA],
    },
    {
      provide: RESOURCE_MANAGER_CONTAINER_ID,
      useFactory: (data: MethodTypeEditorDialogData): string => data.methodTypeId,
      deps: [MAT_DIALOG_DATA],
    },
  ],
})
export class MethodTypeEditorDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional()
    public readonly dialogRef: MatDialogRef<MethodTypeEditorDialogComponent, MethodTypeEditorOutcome | undefined> | null,
    private readonly state: MethodTypeEditorState
  ) {}

  ngOnInit(): void {
    this.state.outcome$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((outcome) => this.dialogRef?.close(outcome));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
